import { Alert, CircularProgress, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { KeyboardEventHandler, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Upload from "../core/Upload";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import RHFAutoComplete from "../core/RHFAutoComplete";
import axios from "axios";
import IResponse from "../../interfaces/response";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CarType from "../../interfaces/car";
import ShowAlert from "./ShowAlert";
import ReportDialog from "../report/ReportDialog";
import { ProfileContext } from "../../ProfileContext";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import RHFNumericFormat from "../core/RHFNumericFormat";
import IFile from "../../interfaces/file";

const carTypes = [
	'ری را ایران خودرو',
	'پژو 206 تیپ 1',
	'پژو 206 تیپ 2',
	'پژو 206 تیپ 5',
	'رنو تندر پلاس دنده ای',
	'رنو تندر پلاس اتوماتیک',
	'رانا پلاس سقف شیشه ای (پانوراما)',
	'پژو 207i صندوقدار اتوماتیک',
	'پژو 405 SLX',
	'سمند SE',
	'رنو تندر 90 E2',
	'پژو 206 تیپ 5',
	'سوزوکی کیزاشی نیمه فول',
	'تارا اتوماتیک',
	'پژو 207i دنده ای',
	'پژو 207',
	'دنا پلاس',
	'ایران خودرو K112',
	'دنا پلاس توربو اتوماتیک',
	'پژو پارس LX TU5',
	'وانت باردو 1600',
	'پژو 407',
	'پژو 206 تیپ 3',
	'رنو تندر 90 E2 دوگانه سوز',
	'سمند LX دوگانه سوز',
	'رنو کپچر',
	'پژو 206 صندوقدار V1',
	'سوزوکی گرند ویتارا 2400',
	'پژو 301',
	'سورن توربو',
	'هایما S7 پلاس',
	'هایما 8S',
	'روآ 1.7L',
	'پژو پارس ELX سفارشی XU7P',
	'آریسان 2',
	'رانا LX',
	'فوتون پیکاپ تونلند',
	'سوزوکی کیزاشی فول اتوماتیک',
	'سوزوکی کیزاشی دنده ای',
	'تارا',
	'سورن پلاس',
	'دنا EF7',
	'پژو 2008',
	'هایما S5',
	'دانگ فنگ S30',
	'فونیکس آریزو 6 پرو',
	'فونیکس تیگو 7 پرو',
	'تیگو ۸ پرو',
	'چری تیگو ۷',
	'چری تیگو ۵',
	'چری آریزو 5',
	'ام وی ام ایکس 55 پرو',
	'ام وی ام ایکس 22 پرو',
	'ام وی ام ایکس 33',
	'ام وی ام ایکس 22',
	'ام وی ام 110',
	'ام وی ام 315 پلاس',
	'سراتو',
	'ریو',
	'کیا اپتیما',
	'کیا اپیروس',
	'کیا سراتو',
	'کیا اسپورتج',
	'کیا پیکانتو',
	'کیا ریو',
	'کیا ریو هاچ بک',
	'کیا سراتو کوپه',
	'کیا سورنتو',
	'کیا سول ',
	'کیا موهاوی',
	'کیا کادنزا',
	'کیا کارنوال',
	'کیا کارنز',
	'کیا استینگر',
	'کیا K5',
	'کیا سلتوس',
	'پراید ۱۱۱',
	'پراید ۱۳۲',
	'پراید ۱۳۱',
	'پراید ۱۴۱',
	'پراید وانت ۱۵۱',
	'تیبا',
	'تیبا ۲',
	'ساینا',
	'کوییک',
	'شاهین',
	'نیسان وانت',
	'مزاد ۳ ',
	'مزدا ۶',
	'مزاد ۳ نیو',
	'مزدا وانت',
	'سایپا آریو',
	'سایپا ایدو',
	'زانتیا',
	'زاگرس',
]

const colors = [
	"سفید",
	"آبی",
	"قرمز",
	"قهوی",
	"زرد",
	"بنفش",
	"مشکی",
	"نقره ای",
]

function getTimeInTehranTimeZone(date: Date) {
	const tehranDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Tehran' }));

	const year = tehranDate.getFullYear();
	const month = String(tehranDate.getMonth() + 1).padStart(2, '0');
	const day = String(tehranDate.getDate()).padStart(2, '0');
	const hours = String(tehranDate.getHours()).padStart(2, '0');
	const minutes = String(tehranDate.getMinutes()).padStart(2, '0');
	const seconds = String(tehranDate.getSeconds()).padStart(2, '0');
	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}


export default function NewCar() {
	const { state } = useLocation();
	const { id } = useParams();
	const [openReport, setOpenReport] = useState(false);
	const { t } = useTranslation();
	const schema = z.object({
		chassis: z.string().min(4),
		license_plate_iran_code: z.string().min(1),
		license_plate: z.string().min(6),
		type: z.string().min(1),
		color: z.string().min(1),
		year: z.string().optional(),
		car_exhibition: z.string().optional(),
		owner_name: z.string().optional(),
		owner_phone: z.string().optional(),
		body_description: z.string().optional(),
		chassis_description: z.string().optional(),
		private_description: z.string().optional(),
		public_description: z.string().optional(),
		price: z.string(),
	})
	const navigate = useNavigate();
	type FormFields = z.infer<typeof schema>

	const { register, handleSubmit, formState: { errors, isSubmitting }, control, getValues, setValue, watch } = useForm<FormFields>({
		resolver: zodResolver(schema),
		defaultValues: {
			chassis: state?.car.chassis ?? '',
            license_plate_iran_code: state?.car.license_plate_iran_code ? state?.car.license_plate_iran_code + '' : '',
            license_plate: state?.car.license_plate ?? '',
            type: state?.car.type ?? '',
            color: state?.car.color ?? '',
            year: state?.car.year ?? '',
            car_exhibition: state?.car.car_exhibition ?? '',
            owner_name: state?.car.owner_name ?? '',
            owner_phone: state?.car.owner_phone ?? '',
            body_description: state?.car.body_description ?? '',
            chassis_description: state?.car.chassis_description ??'',
            private_description: state?.car.private_description ?? '',
            public_description: state?.car.public_description ?? '',
            price: state?.car.price ? state.car.price + '' : '',
		}
	});
	const [bodyStatus, setBodyStatus] = useState<'well' | 'damaged'>('damaged');
	const [chassisStatus, setChassisStatus] = useState<'well' | 'damaged'>('well');
	const [payerType, setPayerType] = useState<'customer' | 'exhibition'>('exhibition');
	const [dateType, setDateType] = useState<'day' | 'desired'>('day');
	const [hourType, sethourType] = useState<'now' | 'desired'>('now');
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<CarType | null>()
	const [images, setImages] = useState<IFile[]>([]);
	const [date, setDate] = useState<Date | null>(new Date);
	const { selectedBranch } = useContext(ProfileContext);

	useEffect(() => {
		if (id) {
			setDateType('desired');
			sethourType('desired');
			setBodyStatus('damaged');
			setChassisStatus('damaged');
		}
		if (id && !state?.car) {
			axios<IResponse<CarType>>({
				url: `/car/${id}`
			}).then((res) => {
				if (res.data.success) {
					setValue('chassis', res.data.data.chassis);
					setValue('license_plate_iran_code', res.data.data.license_plate_iran_code + '');
					setValue('license_plate', res.data.data.license_plate);
					setValue('type', res.data.data.type);
					setValue('color', res.data.data.color);
					if (res.data.data.year) {
						setValue('year', res.data.data.year + '');
					}
					setValue('car_exhibition', res.data.data.car_exhibition ?? '');
					setValue('owner_name', res.data.data.owner_name ?? '');
					setValue('owner_phone', res.data.data.owner_phone ?? '');
					setValue('body_description', res.data.data.body_description ?? '');
					setValue('chassis_description', res.data.data.chassis_description ?? '');
					setValue('public_description', res.data.data.public_description ?? '');
					setValue('price', res.data.data.price + '');
					setValue('private_description', res.data.data.private_description ?? '');
					setDate(new Date(res.data.data.register_date));
				}
			})
		} else if (id && state?.car) {
			setDate(new Date(state?.car.register_date));
		}
	}, [])

	const handleCloseReport = () => {
		setOpenReport(false);
	}

	const handleChangeBodyStatus = (_: any, newValue: 'well' | 'damaged') => {
		if (newValue) {
			setBodyStatus(newValue)
		}
	}

	// const handleChangeChassisStatus = (_: any, newValue: 'well' | 'damaged') => {
	// 	if (newValue) {
	// 		setChassisStatus(newValue)
	// 	}
	// }

	const handleChangePayerType = (_: any, newValue: 'customer' | 'exhibition') => {
		if (newValue) {
			setPayerType(newValue)
		}
	}

	const handleChangeDateType = (_: any, newValue: 'day' | 'desired') => {
		if (newValue) {
			if (newValue === 'day') {
				setDate(new Date);
			}
			setDateType(newValue)
		}
	}

	const handleChangeHourType = (_: any, newValue: 'now' | 'desired') => {
		if (newValue) {
			sethourType(newValue)
		}
	}

	const handleSaveCar: SubmitHandler<FormFields> = async (data) => {
		const res = await axios<IResponse<string>>({
			method: id ? 'PUT' : 'POST',
			url: `/car/${id ?? ''}`,
			data: {
				register_date: getTimeInTehranTimeZone(date ?? new Date),
				price_by: 'customer',
				body_healthy: bodyStatus === "well" ? 1 : 0,
				chassis_healthy: chassisStatus === "well" ? 1 : 0,
				branch_id: selectedBranch,
				images: images.map(img => img.id),
				...data,
				body_description: bodyStatus === 'well' ? 'اتومبیل فوق فاقد رنگ شدگی و شاسی های جلو و عقب سالم میباشد.' : data.body_description,
				// chassis_description: chassisStatus === 'well' ? 'شاسی های جلو و عقب سالم میباشد' : data.chassis_description,
			},
		})
		if (res.data.success) {
			navigate(`/print/${res.data.data}`);
		}
	}

	const handleSubmitChassis: KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			setLoading(true);
			setAlert(undefined)
			axios<IResponse<CarType[]>>({
				url: '/car',
				params: { chassis: getValues('chassis') }
			}).then((res) => {
				if (res.data.success && res.data.data.length) {
					setAlert(res.data.data[0]);
				} else {
					setAlert(null);
				}
			}).finally(() => {
				setLoading(false);
			})
		}
	}

	const handleOpenReport = () => {
		setOpenReport(true);
	}

	const handleChangeDate = (value: Date | null) => {
		setDate(value);
	}

	return (
		<form onSubmit={handleSubmit(handleSaveCar)}>
			<Grid container spacing={2}>
				<Grid xs={12}>
					<ShowAlert branchName={alert?.branch.name ? t('in') + ' ' + alert?.branch.name : undefined} openReport={handleOpenReport} alert={alert ? new Date(alert?.register_date) : alert} />
				</Grid>
				<Grid xs={12}>
					<div className="bg-primary-hover rounded-lg">
						<Grid className='ps-4 py-2' xs={12} container spacing={2}>
							<Grid xs={12} md={8}>
								<TextField {...register('chassis')} InputLabelProps={{ shrink: !!watch('chassis') }} InputProps={{ endAdornment: loading ? <CircularProgress /> : undefined }} onKeyDown={handleSubmitChassis} error={!!errors.chassis} helperText={errors.chassis?.message} fullWidth label={t('chassis_number')} />
							</Grid>
							<Grid xs={12} md={4} className='flex flex-row gap-2 sm:gap-3 md:gap-4 items-center'>
								<Typography>{t('license_plate_number')}:</Typography>
								<TextField {...register('license_plate_iran_code')} error={!!errors.license_plate_iran_code} helperText={errors.license_plate_iran_code?.message} />
								<Typography>{t('iran')}</Typography>
								<TextField {...register('license_plate')} error={!!errors.license_plate} helperText={errors.license_plate?.message} className="grow" />
							</Grid>
							<Grid xs={6} md={4}>
								<RHFAutoComplete name="type" control={control} options={carTypes} fullWidth freeSolo label={t(['car_type'])} />
							</Grid>
							<Grid xs={6} md={4}>
								<RHFAutoComplete name="color" control={control} options={colors} fullWidth freeSolo label={t(['color'])} />
							</Grid>
							<Grid xs={12} md={4}>
								<TextField {...register('year')} InputLabelProps={{ shrink: !!watch('year') }} error={!!errors.year} helperText={errors.year?.message} fullWidth label={t('production_year')} />
							</Grid>
							<Grid xs={12} md={4}>
								<TextField {...register('car_exhibition')} InputLabelProps={{ shrink: !!watch('car_exhibition') }} error={!!errors.car_exhibition} helperText={errors.car_exhibition?.message} fullWidth label={t('carـexhibition')} />
							</Grid>
							<Grid xs={12} md={4}>
								<TextField {...register('owner_name')} InputLabelProps={{ shrink: !!watch('owner_name') }} error={!!errors.owner_name} helperText={errors.owner_name?.message} fullWidth label={t('owner_name')} />
							</Grid>
							<Grid xs={12} md={4}>
								<TextField {...register('owner_phone')} InputLabelProps={{ shrink: !!watch('owner_phone') }} error={!!errors.owner_phone} helperText={errors.owner_phone?.message} fullWidth label={t('owner_phone')} />
							</Grid>
						</Grid>
					</div>
				</Grid>
				<Grid xs={12}>
					<div className="bg-primary-hover rounded-lg p-4 flex flex-col gap-4 sm:gap-6 md:gap-7">
						<div className="flex flex-row gap-16 items-center">
							<Typography>{t('body')}:</Typography>
							<ToggleButtonGroup value={bodyStatus} exclusive onChange={handleChangeBodyStatus}>
								<ToggleButton value="well">
									{t('well')}
								</ToggleButton>
								<ToggleButton value="damaged">
									{t('damaged')}
								</ToggleButton>
							</ToggleButtonGroup>
						</div>
						{
							bodyStatus === 'damaged' && <TextField {...register('body_description')} InputLabelProps={{ shrink: !!watch('body_description') }} error={!!errors.body_description} helperText={errors.body_description?.message} multiline minRows={3} maxRows={5} fullWidth label={t('body_description')} />
						}
					</div>
				</Grid>
				{/* <Grid xs={12} md={6}>
					<div className="bg-primary-hover rounded-lg p-4 flex flex-col gap-4 sm:gap-6 md:gap-7">
						<div className="flex flex-row gap-16 items-center">
							<Typography>{t('chassis')}:</Typography>
							<ToggleButtonGroup value={chassisStatus} exclusive onChange={handleChangeChassisStatus}>
								<ToggleButton value="well">
									{t('well')}
								</ToggleButton>
								<ToggleButton value="damaged">
									{t('damaged')}
								</ToggleButton>
							</ToggleButtonGroup>
						</div>
						{
							chassisStatus === 'damaged' && <TextField {...register('chassis_description')} InputLabelProps={{ shrink: !!watch('chassis_description') }} error={!!errors.chassis_description} helperText={errors.chassis_description?.message} multiline minRows={3} maxRows={5} fullWidth label={t('chassis_description')} />
						}
					</div>
				</Grid> */}
				<Grid xs={12}>
					<div className="bg-primary-hover rounded-lg p-4">
						<TextField {...register('public_description')}  InputLabelProps={{ shrink: !!watch('public_description') }} error={!!errors.public_description} helperText={errors.public_description?.message} minRows={3} maxRows={5} multiline label={t('description_in_paper')} fullWidth />
					</div>
				</Grid>
				<Grid xs={12}>
					<div className="bg-primary-hover rounded-lg p-4 flex flex-col gap-6">
						<TextField {...register('private_description')}  InputLabelProps={{ shrink: !!watch('private_description') }} error={!!errors.private_description} helperText={errors.private_description?.message} minRows={3} maxRows={5} multiline label={t('performer_description')} fullWidth />
						<Alert className="border border-dashed border-info-main" severity="info">
							{t('performer_description_info')}
						</Alert>
					</div>
				</Grid>
				<Grid xs={12} md={6}>
					<div className="bg-primary-hover rounded-lg p-4 flex flex-col gap-6">
						<div className="flex flex-row gap-4 items-center">
							<Typography className="w-11">{t('cost')}:</Typography>
							<ToggleButtonGroup className="w-64" value={payerType} exclusive onChange={handleChangePayerType}>
								<ToggleButton value="customer">
									{t('customer')}
								</ToggleButton>
								<ToggleButton value="exhibition">
									{t('exhibition')}
								</ToggleButton>
							</ToggleButtonGroup>
							<RHFNumericFormat
								name="price"
								control={control}
								className={`grow rounded ${payerType === 'customer' ? 'bg-gray-300' : ''}`}
								disabled={payerType === 'customer'}
								InputProps={{
									endAdornment: <Typography>{t('local_currency')}</Typography>
								}} />
						</div>
						<div className="flex flex-row gap-4 items-center">
							<Typography className="w-11">{t('date')}:</Typography>
							<ToggleButtonGroup className="w-64" value={dateType} exclusive onChange={handleChangeDateType}>
								<ToggleButton value="day">
									{t('day')}
								</ToggleButton>
								<ToggleButton value="desired">
									{t('desired')}
								</ToggleButton>
							</ToggleButtonGroup>
							<DatePicker className={`grow rounded ${dateType === 'day' ? 'bg-gray-300' : ''}`}
								disabled={dateType === 'day'}
								value={date}
								onChange={handleChangeDate} />
						</div>
						<div className="flex flex-row gap-4 items-center">
							<Typography className="w-11">{t('hour')}:</Typography>
							<ToggleButtonGroup className="w-64" value={hourType} exclusive onChange={handleChangeHourType}>
								<ToggleButton value="now">
									{t('now')}
								</ToggleButton>
								<ToggleButton value="desired">
									{t('desired')}
								</ToggleButton>
							</ToggleButtonGroup>
							<TimePicker
								className={`grow rounded ${hourType === 'now' ? 'bg-gray-300' : ''}`}
								disabled={hourType === 'now'}
								onChange={handleChangeDate}
								ampm={false}
								views={['minutes', 'hours']}
								value={date} />
						</div>
					</div>
				</Grid>
				<Grid xs={12} md={6}>
					<div className="flex flex-col gap-6 h-full">
						<Upload defaultUrls={state?.car.images} className="h-full" type="image" multiple text={t('upload_car_images')} setUrls={setImages} />
					</div>
				</Grid>
				<Grid xs={12}>
					<LoadingButton loading={isSubmitting} type="submit" className="mt-4 w-full" variant="contained">{t('submit')}</LoadingButton>
				</Grid>
			</Grid>
			<ReportDialog open={openReport} onClose={handleCloseReport} data={alert!} />
		</form>
	)
}
